import React, { ReactNode } from 'react';
import StatusPill from '../../../../../components/StatusPill';
import { ChevronDownIcon, ChevronRightIcon } from '../../../../../components/IconButton/icons';
import { BALANCE_LEAVE_LABELS, BalanceLeaveType, LeaveYear } from '../../../config';
import { formatLeaveDays, formatLeaveHours, formatSignedHours } from './leaveFormat';

interface LeaveYearsProps {
  leaveType: BalanceLeaveType;
  years: LeaveYear[];
  hoursPerDay: number;
  openYears: Set<number>;
  onToggleYear: (year: number) => void;
  /** What an open year shows under its row: that year's entries. */
  renderDetail: (row: LeaveYear) => ReactNode;
}

/**
 * Leave by calendar year, newest first. Each year is one line of totals that
 * opens to show what made them up.
 *
 * A list of every pay period grows by 26 rows a year; after three years the
 * figure wanted is a long scroll away. A year per line stays short however
 * long someone has worked here.
 *
 * The two leave types read differently in the first and last columns. Annual
 * leave carries over, so a year starts on what the last one ended on. Sick
 * leave starts every year from nothing, and what a closed year had left is
 * the amount that was paid out.
 */
const LeaveYears: React.FC<LeaveYearsProps> = ({
  leaveType,
  years,
  hoursPerDay,
  openYears,
  onToggleYear,
  renderDetail
}) => {
  const isSick = leaveType === 'sick_leave';
  const label = BALANCE_LEAVE_LABELS[leaveType].toLowerCase();

  if (years.length === 0) {
    return <p className="ui-table__empty">No {label} to show yet.</p>;
  }

  return (
    <div className="leave-years">
      <div className="leave-years__head" aria-hidden="true">
        <span />
        <span>Year</span>
        <span className="is-num">{isSick ? 'Opening' : 'Start of year'}</span>
        <span className="is-num">Earned</span>
        <span className="is-num">Adjusted</span>
        <span className="is-num">Taken</span>
        <span className="is-num">{isSick ? 'Remaining' : 'Balance'}</span>
      </div>

      {years.map((row) => {
        const isOpen = openYears.has(row.year);
        const startOfYear = Number(row.brought_forward) + Number(row.opening);
        const remaining = Number(row.remaining);
        const adjustments = Number(row.adjustments);
        const detailId = `leave-year-${leaveType}-${row.year}`;

        return (
          <div key={row.year} className={`leave-year ${isOpen ? 'is-open' : ''}`.trim()}>
            <button
              type="button"
              className="leave-year__summary"
              aria-expanded={isOpen}
              aria-controls={detailId}
              onClick={() => onToggleYear(row.year)}
            >
              <span className="leave-year__chevron" aria-hidden="true">
                {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
              </span>
              <span className="leave-year__year">
                {row.year}
                {row.is_current
                  ? <StatusPill label="This year" tone="success" />
                  : isSick && <StatusPill label="Closed" />}
              </span>
              <span className="is-num" aria-label={isSick ? 'Opening' : 'Start of year'}>
                {startOfYear !== 0
                  ? formatLeaveHours(startOfYear)
                  : <span className="ui-table__na">—</span>}
              </span>
              <span className="is-num" aria-label="Earned">{formatLeaveHours(Number(row.accrued))}</span>
              <span className="is-num" aria-label="Adjusted">
                {adjustments !== 0
                  ? formatSignedHours(adjustments)
                  : <span className="ui-table__na">—</span>}
              </span>
              <span className="is-num" aria-label="Taken">{formatLeaveHours(Number(row.taken))}</span>
              <span
                className={`is-num leave-year__remaining ${remaining < 0 ? 'leave-tab__negative' : ''}`.trim()}
                aria-label={isSick ? 'Remaining' : 'Balance'}
              >
                <strong>{formatLeaveHours(remaining)}</strong>
                <span className="leave-year__days">{formatLeaveDays(remaining, hoursPerDay)}</span>
              </span>
            </button>

            {isOpen && (
              <div className="leave-year__detail" id={detailId}>
                {Number(row.pending) > 0 && (
                  <p className="leave-year__pending">
                    {formatLeaveHours(Number(row.pending))} more is in a week that has not been
                    submitted, and is not yet taken off.
                  </p>
                )}
                {renderDetail(row)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LeaveYears;
